import React, { useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Popover,
  Typography,
  Badge,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";

const TopicFilters = ({ filters, onFilterChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "filters-popover" : undefined;

  // Count active filters (excluding default values)
  const activeFiltersCount = [
    filters.platform !== "",
    filters.sortBy !== "newest",
    filters.pageSize !== 10,
    filters.type !== "",
  ].filter(Boolean).length;

  return (
    <>
      <Badge
        badgeContent={activeFiltersCount}
        color="error"
        sx={{
          "& .MuiBadge-badge": {
            fontFamily: "Host Grotesk, sans-serif",
            fontWeight: 600,
          },
        }}
      >
        <IconButton
          onClick={handleClick}
          sx={{
            width: "44px",
            height: "44px",
            background: "linear-gradient(180deg, #4079f0 0%, #2a5fe6 100%)",
            color: "white",
            borderRadius: "12px",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(53, 114, 239, 0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #2a5fe6 0%, #1d4ed8 100%)",
              boxShadow: "0 6px 16px rgba(53, 114, 239, 0.45)",
              transform: "translateY(-1px)",
            },
          }}
          aria-describedby={id}
        >
          <FontAwesomeIcon icon={faFilter} />
        </IconButton>
      </Badge>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            border: "1px solid #e5e7eb",
            minWidth: "280px",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
              borderBottom: "2px solid #f3f4f6",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                background: "linear-gradient(135deg, #6c63ff 0%, #5a52d5 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Filters
            </Typography>
            {activeFiltersCount > 0 && (
              <Typography
                variant="caption"
                onClick={() => {
                  onFilterChange("platform", "");
                  onFilterChange("sortBy", "newest");
                  onFilterChange("pageSize", 10);
                  onFilterChange("type", "");
                }}
                sx={{
                  fontFamily: "Host Grotesk, sans-serif",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#ef4444",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Clear All
              </Typography>
            )}
          </Box>
          {/* Type Filter */}
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: "white",
                fontFamily: "Host Grotesk, sans-serif",
                "&:hover fieldset": {
                  borderColor: "#6c63ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#6c63ff",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 500,
              },
            }}
          >
            <InputLabel id="type-filter-label">Objective</InputLabel>

            <Select
              labelId="type-filter-label"
              id="type-filter"
              value={filters.type}
              label="Objective"
              onChange={(e) => onFilterChange("type", e.target.value)}
            >
              <MenuItem value="">
                <em>All Types</em>     
              </MenuItem>

              <MenuItem value="sentiment">Sentiment</MenuItem>
              <MenuItem value="section">Section</MenuItem>
            </Select>
          </FormControl>

          {/* Platform Filter */}
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: "white",
                fontFamily: "Host Grotesk, sans-serif",
                "&:hover fieldset": {
                  borderColor: "#6c63ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#6c63ff",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 500,
              },
            }}
          >
            <InputLabel id="platform-filter-label">Platform</InputLabel>
            <Select
              labelId="platform-filter-label"
              id="platform-filter"
              value={filters.platform}
              label="Platform"
              onChange={(e) => onFilterChange("platform", e.target.value)}
            >
              <MenuItem value="">
                <em>All Platforms</em>
              </MenuItem>
              <MenuItem value="twitter">Twitter</MenuItem>
              <MenuItem value="facebook">Facebook</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="linkedin">LinkedIn</MenuItem>
              <MenuItem value="youtube">YouTube</MenuItem>
              <MenuItem value="reddit">Reddit</MenuItem>
              <MenuItem value="tiktok">TikTok</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: "white",
                fontFamily: "Host Grotesk, sans-serif",
                "&:hover fieldset": {
                  borderColor: "#6c63ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#6c63ff",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 500,
              },
            }}
          >
            <InputLabel id="sort-filter-label">Sort By</InputLabel>
            <Select
              labelId="sort-filter-label"
              id="sort-filter"
              value={filters.sortBy}
              label="Sort By"
              onChange={(e) => onFilterChange("sortBy", e.target.value)}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
              <MenuItem value="most_counts">Most Posts</MenuItem>
            </Select>
          </FormControl>

          {/* Page Size Filter */}
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: "white",
                fontFamily: "Host Grotesk, sans-serif",
                "&:hover fieldset": {
                  borderColor: "#6c63ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#6c63ff",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 500,
              },
            }}
          >
            <InputLabel id="pagesize-filter-label">Per Page</InputLabel>
            <Select
              labelId="pagesize-filter-label"
              id="pagesize-filter"
              value={filters.pageSize}
              label="Per Page"
              onChange={(e) =>
                onFilterChange("pageSize", parseInt(e.target.value))
              }
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Popover>
    </>
  );
};

export default TopicFilters;
